<template>
  <Txt class="mt-4 flex items-center gap-2 text-base font-medium">{{ $t('Company.UserManagement') }}</Txt>
  <p class="text-sm text-gray-600">{{ $t('Company.UserManagementDescription')}}</p>

  <div class="space-y-8">
    <CGBanner
      :title="$t('Company.UserManagement')"
      :subtitle="$t('Company.UserManagementBannerDescription')"
      variant="cyan"
    >
      <template #icon>
        <Icon name="user" size="22" class="text-cyan-600" />
      </template>
    </CGBanner>

    <div class="space-y-6">
      <h3 class="text-lg font-semibold flex items-center space-x-2">
        <font-awesome-icon :icon="['fas','gear']" class="text-blue-800 w-5 h-5" />
        <span class="pt-1">{{ $t('Company.ProvisioningType') }}</span>
      </h3>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Traditional method, show banner -->
        <RadioCard
          :id="ProvisioningType.Manual"
          v-model="draftCompany.provisioningType"
          :title="$t('Company.ProvisioningManual')"
          :subtitle="$t('Company.ProvisioningManualDescription')"
          variant="orange"
        >
          <template #icon>
            <font-awesome-icon :icon="['fas','users']" class="text-white w-8 h-8" />
          </template>
        </RadioCard>

        <!-- SCIM, show configuration -->
        <RadioCard
          :id="ProvisioningType.Automatic"
          v-model="draftCompany.provisioningType"
          :title="$t('Company.ProvisioningAutomatic')"
          :subtitle="$t('Company.ProvisioningAutomaticDescription')"
          variant="green"
        >
          <template #icon>
            <font-awesome-icon :icon="['fas','bullseye']" class="text-white w-8 h-8" />
          </template>
        </RadioCard>
      </div>
    </div>

    <CGBanner
      v-if="draftCompany?.provisioningType === ProvisioningType.Manual"
      :title="$t('Company.ManualManagement')"
      :subtitle="$t('Company.ManualManagementDescription')"
      variant="orange"
    >
      <template #icon>
        <font-awesome-icon :icon="['fas','upload']" class="text-orange-600" />
      </template>
    </CGBanner>

    <div v-else class="cyber-card p-6 bg-green-50 border-green-200">
      <h3 class="font-semibold text-green-900 mb-6 flex items-center space-x-2">
        <font-awesome-icon :icon="['fas','gear']" class="text-green-800 w-5 h-5" />
        <span>{{ $t('Company.SCIMConfig') }}</span>
      </h3>
      <div class="space-y-6">
        <CGBanner
          :title="$t('Company.SCIMEnabled')"
          :subtitle="$t('Company.SCIMEnabledDescription')"
          variant="green"
        >
          <template #icon>
            <Icon name="info" size="24" class="text-green-600" />
          </template>
        </CGBanner>

        <div class="space-y-2">
          <FormGroup class="mt-4" html-for="urlSCIM" :label="$t('Company.URLSCIM')">
            <CGInput
              id="urlSCIM"
              v-model="scimURL"
              disabled
              :placeholder="$t('Company.URLSCIMPlaceholder')"
            ></CGInput>
          </FormGroup>
          <Txt class="mt-5 text-gray-700 text-xs">{{ $t('Company.URLSCIMInfo') }}</Txt>
        </div>
      </div>
    </div>

    <div class="space-y-6"> 
      <h3 class="text-lg font-semibold flex items-center space-x-2">
        <div class="w-5 h-5 text-purple-800">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 11h4M8 9v4M15 12h.01M18 10h.01M17.32 5H6.68A4 4 0 0 0 2.68 8.74c-.01.05-.01.1-.02.15C2.6 9.42 2 14.46 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.41-1.41A2 2 0 0 1 9.83 16h4.34a2 2 0 0 1 1.41.59L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.54-.6-6.58-.68-7.26-.01-.05-.01-.1-.02-.15A4 4 0 0 0 17.32 5z"/>
          </svg>
        </div>
        <span class="pt-1">{{ $t('Company.Gamification') }}</span>
      </h3>

      <div class="cyber-card p-6">
        <div class="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
          <div class="space-y-1 w-full">
            <div class="flex justify-between items-center">
              <div class="flex flex-col text-left text-sm">
                <span class="font-medium"><font-awesome-icon :icon="['fas','trophy']" class="text-purple-600 mr-4" />{{ $t('Company.EnableGamification') }}</span>
                {{ $t('Company.EnableGamificationDescription') }}
              </div>
              <Toggle v-model="draftCompany.enableGamification" class="ml-4" />
            </div>
          </div>
        </div>

        <div v-if="!draftCompany.enableGamification" class="mt-6 text-center py-8">
          <!-- Move this icon to icons.js file after simplifying the path -->
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-12 h-12 mx-auto mb-3 opacity-30" aria-hidden>
            <path d="M6 11h4M8 9v4M15 12h.01M18 10h.01M17.32 5H6.68A4 4 0 0 0 2.68 8.74c-.01.05-.01.1-.02.15C2.6 9.42 2 14.46 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.41-1.41A2 2 0 0 1 9.83 16h4.34a2 2 0 0 1 1.41.59L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.54-.6-6.58-.68-7.26-.01-.05-.01-.1-.02-.15A4 4 0 0 0 17.32 5z"/>
          </svg>
          <h4 class="text-lg font-medium mb-2">{{ $t('Company.GamificationDisabled')}}</h4>
          <p class="text-sm">{{ $t('Company.GamificationDisabledDescription')}}</p>
        </div>

        <div v-else class="space-y-2">
          <CGBanner
            :title="$t('Company.GamificationCategory')"
            :subtitle="$t('Company.GamificationCategoryDescription')"
            variant="purple"
          >
            <template #icon>
              <Icon name="info" size="22" class="text-purple-600" />
            </template>
          </CGBanner>
          <FormGroup class="mt-4" html-for="org" :label="$t('Company.GamificationCategoryName')">
            <CGInput
              id="org"
              v-model="draftCompany.orgName"
              :placeholder="$t('Company.GamificationCategoryPlaceholder')"
            ></CGInput>
          </FormGroup>
          <Txt class="mt-5 text-gray-700 text-xs">{{ $t('Company.GamificationCategoryInfo') }}</Txt>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { ProvisioningType } from '@/common/constants';

export default defineComponent({
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      ProvisioningType,
      draftCompany: { ...this.company }
    };
  },
  watch: {
    company: {
      handler(newVal) {
        this.draftCompany = { ...newVal };
      },
      deep: true
    }
  },
  computed: {
    scimURL() {
      const cgHost = window.location.host;
      const cg2Domain = cgHost.includes('.cyberguru.') ? cgHost.split('.').slice(1).join('.') : 'develop.cyberguru.dev';
      const companyName = this.draftCompany.name || 'default-company';
      return `https://scim.${companyName}.${cg2Domain}/scim/v2`;
    }
  }
});
</script>